import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    if (!slug || slug === 'all') return comics;
    return comics.filter((c) => c.categorySlug === slug);
  }, [slug]);

  const categoryName = category?.name || '全部漫画';

  useEffect(() => {
    document.title = `${categoryName} - 免费韩国漫画分类浏览 - 韩漫网`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        `韩漫网${categoryName}分类，浏览海量免费${categoryName}类韩国漫画。高清画质，每日更新，畅享流畅的漫画在线阅读体验。`
      );
    }
  }, [categoryName]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryName} - 韩漫网`,
            description: `韩漫网${categoryName}分类漫画列表`,
            url: `http://yxhaomai.com/category/${slug}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://yxhaomai.com/' },
              { '@type': 'ListItem', position: 2, name: categoryName, item: `http://yxhaomai.com/category/${slug}` },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <Link to="/category/all" className="hover:text-primary-600 transition-colors cursor-pointer">分类</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">{categoryName}</span>
              </nav>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                {categoryName}漫画
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                浏览{categoryName}分类下的所有韩国漫画，发现更多精彩作品
              </p>

              <div className="flex items-center gap-2 mt-6 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画，请浏览其他分类</p>
                  <Link to="/category/all" className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                    查看全部漫画
                  </Link>
                </div>
              )}
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                <div className="text-center p-6">
                  <div className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">
                    {filteredComics.length}
                  </div>
                  <p className="text-xs text-foreground-500">本类漫画数量</p>
                </div>
                <div className="text-center p-6">
                  <div className="text-3xl md:text-4xl font-bold text-accent-600 font-heading mb-2">
                    每周更新
                  </div>
                  <p className="text-xs text-foreground-500">更新频率</p>
                </div>
                <div className="text-center p-6">
                  <div className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">
                    {Math.round(filteredComics.filter((c) => c.status === '已完结').length / Math.max(filteredComics.length, 1) * 100)}%
                  </div>
                  <p className="text-xs text-foreground-500">完结率</p>
                </div>
                <div className="text-center p-6">
                  <div className="text-3xl md:text-4xl font-bold text-accent-600 font-heading mb-2">
                    {(filteredComics.length > 0
                      ? (filteredComics.reduce((sum, c) => sum + c.rating, 0) / filteredComics.length).toFixed(1)
                      : '0.0')}
                  </div>
                  <p className="text-xs text-foreground-500">平均评分</p>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}